<script setup lang="ts">
// Section-stack SPA per the product plan: Hero → Common Problems → Guided
// Diagnostic → AI Results → Education → Lead Capture → Footer.
// Navigation scrolls between sections; results appear once a diagnosis lands.
import { nextTick, ref } from 'vue'
import SiteConfigProvider from '@/shared/naive/SiteConfigProvider.vue'
import SiteFooter from '@/shared/components/SiteFooter.vue'
import PhotoBand from '@/shared/components/PhotoBand.vue'
// Pixabay Content License (free commercial use, no attribution required).
import comfortPhoto from '@/assets/ror-comfort.jpg'
import { declareReplacement } from '@/shared/state/leadIntent'
import type { DiagnosisResponse } from '@/shared/api/types'
import HeroSection from './sections/HeroSection.vue'
import CommonProblems from './sections/CommonProblems.vue'
import GuidedDiagnostic from './sections/GuidedDiagnostic.vue'
import AiResults from './sections/AiResults.vue'
import EducationSection from './sections/EducationSection.vue'
import LeadSection from './sections/LeadSection.vue'

const diagnostic = ref<InstanceType<typeof GuidedDiagnostic> | null>(null)
const diagnosis = ref<DiagnosisResponse | null>(null)

function scrollTo(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
}

function onSelectProblem(symptom: string) {
    diagnostic.value?.startWithSymptom(symptom)
    scrollTo('diagnosis')
}

async function onResult(res: DiagnosisResponse) {
    diagnosis.value = res
    await nextTick()
    scrollTo('results')
}

const NAV = [
    { id: 'problems', label: 'Common Problems' },
    { id: 'diagnosis', label: 'Diagnosis' },
    { id: 'learn', label: 'Learn' },
    { id: 'pro', label: 'Talk to a Pro' },
]
</script>

<template>
    <SiteConfigProvider>
        <header class="topbar">
            <button class="brand" type="button" @click="scrollTo('top')">
                <svg class="mark" viewBox="0 0 28 28" aria-hidden="true">
                    <circle cx="14" cy="14" r="12.5" fill="none" stroke="#2a2f38" stroke-width="3" />
                    <path d="M3.6 19.5 A 12.5 12.5 0 0 1 14 1.5 A 12.5 12.5 0 0 1 24.4 19.5" fill="none" stroke="#3ddc84" stroke-width="3" stroke-linecap="round" />
                    <line x1="14" y1="14" x2="20.5" y2="8.5" stroke="#3ddc84" stroke-width="2.5" stroke-linecap="round" />
                    <circle cx="14" cy="14" r="2.4" fill="#3ddc84" />
                </svg>
                <span class="word">Repair<span>Or</span>Replace<span>.AI</span></span>
            </button>
            <nav>
                <button
                    v-for="item in NAV.filter((i) => i.id !== 'diagnosis')"
                    :key="item.id"
                    type="button"
                    @click="scrollTo(item.id)"
                >
                    {{ item.label }}
                </button>
            </nav>
            <button class="cta nav-cta" type="button" @click="scrollTo('diagnosis')">
                Start Diagnosis
            </button>
        </header>

        <main>
            <HeroSection
                @start="scrollTo('diagnosis')"
                @browse="scrollTo('problems')"
                @replace="declareReplacement('pro')"
            />
            <CommonProblems @select="onSelectProblem" />
            <GuidedDiagnostic ref="diagnostic" @result="onResult" />
            <AiResults v-if="diagnosis" :response="diagnosis" />
            <EducationSection />
            <PhotoBand
                :image="comfortPhoto"
                overlay="linear-gradient(180deg, rgba(19, 21, 25, 0.88) 0%, rgba(19, 21, 25, 0.55) 50%, rgba(19, 21, 25, 0.92) 100%)"
            >
                <p class="band-head">Comfort is the whole point.</p>
                <p class="band-sub">
                    Whatever the verdict — repair, replace, or wait — the goal is a house that
                    just feels right. Still on the fence? Put a human on it.
                </p>
                <button class="cta band-cta" type="button" @click="scrollTo('pro')">
                    Talk to a Pro
                </button>
            </PhotoBand>
            <LeadSection :diagnosis-session-id="diagnosis?.session_id" />
        </main>

        <SiteFooter
            site-name="RepairOrReplace.AI"
            tagline="AI-powered second opinions on the repair-or-replace question — evidence first, zero sales pressure."
        />
    </SiteConfigProvider>
</template>

<style scoped lang="scss">
.topbar {
    position: sticky;
    top: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--site-space-md);
    padding: var(--site-space-sm) var(--site-space-lg);
    background: var(--site-glass-bg);
    border-bottom: 1px solid var(--site-glass-border);
    backdrop-filter: blur(var(--site-glass-blur));
    -webkit-backdrop-filter: blur(var(--site-glass-blur));

    .brand {
        display: inline-flex;
        align-items: center;
        gap: 0.55rem;
        background: none;
        border: none;
        cursor: pointer;
        color: var(--site-text);
        font-family: var(--site-font-heading);
        font-size: 1.1rem;
        font-weight: 800;
        letter-spacing: -0.02em;

        .mark {
            width: 1.7rem;
            height: 1.7rem;
        }

        .word {
            color: var(--site-text);

            span {
                color: var(--site-accent);
            }
        }
    }

    .nav-cta {
        padding: 0.5em 1.2em;
        font-size: 0.9rem;
    }

    nav {
        display: flex;
        gap: var(--site-space-md);

        button {
            background: none;
            border: none;
            cursor: pointer;
            color: var(--site-text-muted);
            font-family: var(--site-font-body);
            font-size: 0.95rem;

            &:hover {
                color: var(--site-accent);
            }
        }

        @media (max-width: 700px) {
            display: none;
        }
    }
}
</style>


<style lang="scss">
// Band typography (unscoped: PhotoBand renders the slot).
#learn + .photo-band {
    .band-head {
        font-family: var(--site-font-heading);
        font-size: clamp(1.9rem, 4.5vw, 3rem);
        font-weight: 800;
        letter-spacing: -0.02em;
        color: #fff;
        margin: 0;
        text-shadow: 0 2px 24px rgba(0, 0, 0, 0.5);
    }

    .band-sub {
        color: rgba(255, 255, 255, 0.82);
        font-size: 1.1rem;
        max-width: 48ch;
        margin: 0;
    }

    .band-cta {
        margin-top: var(--site-space-sm);
        font-size: 1.05rem;
    }
}
</style>
